import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";

// Hook to manage table data, fetching, and live refresh
export const useTableData = (tableName, isOpen) => {
    const [tableData, setTableData] = useState([]);
    const [filteredTableData, setFilteredTableData] = useState([]);
    const [loadingTableData, setLoadingTableData] = useState(false);
    const [tableDataError, setTableDataError] = useState(null);
    
    // Live refresh state
    const [liveRefresh, setLiveRefresh] = useState(false);
    const liveRefreshIntervalRef = useRef(null);
    
    // Fetch table data
    const fetchTableData = useCallback(async (table) => {
        if (!table) return;
        
        try {
            setLoadingTableData(true);
            setTableDataError(null);
            
            // Query to get data from the selected table (limited to 100 rows)
            const sqlQuery = `SELECT * FROM ${table} LIMIT 100`;
            
            const response = await axios.post("/query", sqlQuery, {
                headers: {
                    "Content-Type": "text/plain",
                    "Authorization": `Bearer ${localStorage.getItem('authToken')}`
                }
            });
            
            if (response.data.isSuccess && response.data.data) {
                setTableData(response.data.data);
                setFilteredTableData(response.data.data);
            } else {
                setTableDataError("Failed to fetch table data");
            }
        } catch (error) {
            setTableDataError(error.response?.data?.message || "Failed to fetch table data");
        } finally {
            setLoadingTableData(false);
        }
    }, []);
    
    // Fetch data when table name changes
    useEffect(() => {
        if (tableName && isOpen) {
            fetchTableData(tableName);
        }
    }, [tableName, isOpen, fetchTableData]);
    
    // Toggle live refresh mode
    const toggleLiveRefresh = useCallback(() => {
        setLiveRefresh(prevState => {
            const newState = !prevState;
            
            // Start interval if turning on, clear if turning off
            if (newState) {
                liveRefreshIntervalRef.current = setInterval(() => {
                    if (tableName) {
                        fetchTableData(tableName);
                    }
                }, 5000); // Refresh every 5 seconds
            } else if (liveRefreshIntervalRef.current) {
                clearInterval(liveRefreshIntervalRef.current);
                liveRefreshIntervalRef.current = null;
            }
            
            return newState;
        });
    }, [tableName, fetchTableData]);
    
    // Clean up interval on unmount or when panel closes
    useEffect(() => {
        return () => {
            if (liveRefreshIntervalRef.current) {
                clearInterval(liveRefreshIntervalRef.current);
                liveRefreshIntervalRef.current = null;
            }
        };
    }, []);
    
    // Also clean up when panel closes
    useEffect(() => {
        if (!isOpen && liveRefreshIntervalRef.current) {
            clearInterval(liveRefreshIntervalRef.current);
            liveRefreshIntervalRef.current = null;
            setLiveRefresh(false);
        }
    }, [isOpen]);
    
    return {
        tableData,
        filteredTableData,
        setFilteredTableData,
        loadingTableData,
        tableDataError,
        fetchTableData,
        liveRefresh,
        toggleLiveRefresh
    };
};